import { DocumentKind } from './document-kind'
import { FolderLook } from './folder-look'

/**
 * Qué miniatura DIBUJADA le toca a cada documento y a cada carpeta.
 *
 * ## Por qué las miniaturas se dibujan en vez de renderizarse
 *
 * La maqueta muestra el interior del papel: renglones, la retícula de un plano. Renderizar eso de
 * verdad es descargar el PDF entero para pintar 62 píxeles — en datos móviles no se paga solo. El
 * arte generado da el mismo reconocimiento a costo cero, y es el MISMO truco de la maqueta: sus
 * miniaturas también son CSS, no vistas previas.
 *
 * ## De dónde sale la decisión
 *
 * Del cruce de dos datos que ya existen: el `mimeType` del documento (vía `documentKind`) y la
 * pinta de la carpeta que lo contiene (`folderLook`). Un PDF suelto es una hoja de texto; el MISMO
 * PDF adentro de «Planos» es un plano — el contrato no distingue esos casos, la carpeta sí.
 */

export const DocumentArt = {
  /** Hoja de texto con renglones. El documento genérico. */
  pdf: 'pdf',
  /** Retícula de plano. */
  plano: 'plano',
  /** Marco de fotografía. */
  imagen: 'imagen'
} as const

export type DocumentArt = (typeof DocumentArt)[keyof typeof DocumentArt]

export function documentArt(
  kind: DocumentKind,
  look: FolderLook = FolderLook.generic
): DocumentArt {
  // La imagen manda sobre la carpeta: una foto guardada en «Planos» sigue siendo una foto.
  if (kind === DocumentKind.image) return DocumentArt.imagen
  if (kind === DocumentKind.pdf && look === FolderLook.plans) return DocumentArt.plano

  // `other` también cae acá: Word, Excel, lo indeterminable. La hoja de texto es el dibujo honesto
  // para «un documento» sin afirmar el formato.
  return DocumentArt.pdf
}

/** La portada de una carpeta en la grilla del cartapacio. */
export const FolderCover = {
  /** Hoja con esquina doblada y renglones: contratos, legales, lo administrativo. */
  contrato: 'contrato',
  /** Retícula de plano. */
  plano: 'plano',
  /** Librillo con el sello ámbar: reglamentos. */
  reglamento: 'reglamento',
  /** Cámara: la carpeta de fotos. Sin miniatura real hasta que el contrato publique imágenes. */
  fotos: 'fotos'
} as const

export type FolderCover = (typeof FolderCover)[keyof typeof FolderCover]

export function folderCover(look: FolderLook): FolderCover {
  switch (look) {
    case FolderLook.plans:
      return FolderCover.plano
    case FolderLook.regulations:
      return FolderCover.reglamento
    case FolderLook.photos:
      return FolderCover.fotos
    default:
      return FolderCover.contrato
  }
}
